import { Component, Element, Event, EventEmitter, h, Prop, State } from '@stencil/core';
import state from '~store/search-store';
import { SuggestResponseItem } from '~model/search/suggest-response-item';
import { HistoryService } from '~service/history-service';
import { SearchService } from '~service/search-service';
import { translate } from '~service/translation-service';
import { debounce } from '~service/utilities';

const NO_SUGGESTION_SELECTED = -1;

@Component({
  tag: 'mama-autosuggest-box',
})
export class AutoSearchBox {
  @Element() el: HTMLElement;

  @Prop() useSearchIcon: boolean = false;
  @Prop() buttonLabel: string = 'search.searchBox.button';

  @State() q = '';
  @State() suggestions: SuggestResponseItem[] = [];
  @State() suggestionsCursor: number = NO_SUGGESTION_SELECTED;

  @Event({
    eventName: 'autoSuggestBoxSearch',
    composed: true,
    cancelable: true,
    bubbles: true,
  })
  autoSuggestBoxSearch: EventEmitter<string>;

  searchForResults = debounce(async q => {
    if (q.length >= 3) {
      this.suggestions = await SearchService.globalSuggest(q);
    } else {
      this.suggestions = [];
    }
  }, 200);

  normalizePath(path): string {
    return path.replace(/\/$/, '');
  }

  handleSelect(name) {
    const isSearchPage = this.normalizePath(window.location.pathname) == this.normalizePath(state.searchUrl);

    if (isSearchPage) {
      this.autoSuggestBoxSearch.emit(name);
    }

    this.q = name;
    this.suggestions = [];
    this.suggestionsCursor = NO_SUGGESTION_SELECTED;

    if (!isSearchPage) {
      HistoryService.addArgumentToUrl(state.searchUrl, { 'tx_solr[q]': name });
      window.location.reload();
    }
  }

  handleSubmit(event) {
    event.preventDefault();
    this.handleSelect(this.q);
  }

  handleBlur() {
    this.suggestions = [];
  }

  handleFocus() {
    this.searchForResults(this.q);
  }

  handleKeyDown(event: KeyboardEvent) {
    if (!this.suggestions.length) {
      return;
    }
    switch (event.key.toLocaleLowerCase()) {
      case 'arrowup':
      case 'up':
        if (this.suggestionsCursor > NO_SUGGESTION_SELECTED) {
          this.suggestionsCursor -= 1;
        }

        if (this.suggestions[this.suggestionsCursor]) {
          this.q = this.suggestions[this.suggestionsCursor].name;
        }
        event.preventDefault();
        break;
      case 'arrowdown':
      case 'down':
        if (this.suggestionsCursor < this.suggestions.length - 1) {
          this.suggestionsCursor += 1;
        }

        if (this.suggestions[this.suggestionsCursor]) {
          this.q = this.suggestions[this.suggestionsCursor].name;
        }

        event.preventDefault();
        break;
    }
  }

  handleChange(event) {
    this.q = event.target.value;
    this.searchForResults(this.q);
    this.suggestionsCursor = NO_SUGGESTION_SELECTED;
  }

  handleHover(suggestion) {
    this.suggestionsCursor = this.suggestions.indexOf(suggestion);
  }

  renderSuggestions() {
    if (!this.suggestions.length) {
      return;
    }

    const suggestions = this.suggestions.map((suggestion, index) => {
      const props = index === this.suggestionsCursor ? { class: 'active' } : { class: 'inactive' };
      return (
        <li {...props} onMouseDown={_ => this.handleSelect(suggestion.name)} onMouseEnter={_ => this.handleHover(suggestion)}>
          {suggestion.name} ({suggestion.count})
        </li>
      );
    });

    return (
      <ul class="autocomplete">
        <li class="title">{translate('search.searchBox.suggestions')}</li>
        {suggestions}
      </ul>
    );
  }

  renderButton() {
    if (this.useSearchIcon) {
      return (
        <button class="btn btn-search" role="button" onClick={event => this.handleSubmit(event)}>
          <svg class="search">
            <use xlinkHref="#search"></use>
          </svg>
        </button>
      );
    }

    return (
      <button class="btn btn-light" onClick={event => this.handleSubmit(event)}>
        {translate(this.buttonLabel)}
      </button>
    );
  }

  render() {
    return [
      <form class={this.useSearchIcon ? 'icon-search' : ''} role="search" onSubmit={event => this.handleSubmit(event)}>
        <label htmlFor="mama-autosuggest-box" hidden>
          {translate('search.searchBox.placeholder')}
        </label>
        <input
          type="search"
          class="form-control"
          name="tx_solr[q]"
          value={this.q}
          placeholder={translate('search.searchBox.placeholder')}
          aria-label={translate('search.searchBox.placeholder')}
          onInput={event => this.handleChange(event)}
          onBlur={_ => this.handleBlur()}
          onFocus={_ => this.handleFocus()}
          onKeyDown={event => this.handleKeyDown(event)}
          autocomplete="off"
          id="mama-autosuggest-box"
        />
        {this.renderButton()}
      </form>,
      this.renderSuggestions(),
    ];
  }
}
